import { Box, Drawer, DrawerProps, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../hooks/UserContext";
import { AppVersion } from "../helpers";
import { API } from "../../api";
import { AuthUser } from "../../api/common";
import { setJwtToken, setRefreshToken } from "../../utilities/utils";

interface ItemBreakProps {
	style?: React.CSSProperties;
}

const ItemBreak = React.memo(function ItemBreak(props: ItemBreakProps) {
	return (
		<ListItem
			style={{ borderBottomStyle: "solid", borderBottomColor: "#d3d3d3", borderBottomWidth: "1px", ...props.style }}
		/>
	);
});

function logout() {
	setJwtToken("");
	setRefreshToken("");
	localStorage.clear();
	sessionStorage.clear();
	window.location.href = "/login";
}

export type NavLink = {
	to: string;
	text: string;
};
interface DrawerNavigationProps extends DrawerProps {
	onNavigate: (link: NavLink) => void;
	setNavigationDrawerState: (open: boolean) => void;
}

export const DrawerNavigation = (props: DrawerNavigationProps) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const userContext = React.useContext(UserContext) as AuthUser | undefined;
	const userLinks: NavLink[] = [];
	if (!userContext?.id) {
		userLinks.push({ to: "/login", text: "Login" });
	} else {
		userLinks.push({ to: "/home", text: "Home" });
		userLinks.push({ to: "/jobs/dashboard", text: "Jobs" });
		userLinks.push({ to: "/jobs/submit", text: "Submit Jobs" });
		userLinks.push({ to: "/jobs/ready", text: "Mark Jobs Ready" });
	}
	const adminLinks: NavLink[] = [];
	if (userContext?.isSuperUser) {
		adminLinks.push({ to: "/admin/sortshp", text: "Reorganize Shapefiles" });
		adminLinks.push({ to: "/admin/import", text: "Import Settings" });
		adminLinks.push({ to: "/admin/geofile-converter", text: "GeoFile Converter" });
		adminLinks.push({ to: "/admin/mission-control-logs", text: "Mission Control Logs" });
	}

	const companyParam = searchParams.get("company");
	const buildNavPath = (path: string) => (companyParam ? `${path}?company=${companyParam}` : path);

	// oxlint-disable-next-line no-unused-vars
	const { setNavigationDrawerState, onNavigate, ...drawerProps } = props;
	return (
		<Drawer style={{ zIndex: 1000 }} anchor="left" variant="temporary" {...drawerProps}>
			<Box //style={{ marginTop: 80 }}
				sx={{ width: 250 }}
				role="presentation"
				flexDirection={"column"}
			>
				<List>
					{userLinks.map((link) => (
						<ListItem key={link.text} disablePadding>
							<ListItemButton
								onClick={() => {
									props.onNavigate(link);
									navigate(buildNavPath(link.to), {
										state: { title: link.text },
									});
								}}
							>
								<ListItemText primary={link.text} />
							</ListItemButton>
						</ListItem>
					))}
					{userContext?.id && (
						<ListItem key={"logout"} disablePadding>
							<ListItemButton onClick={logout}>
								<ListItemText primary={"Logout"} />
							</ListItemButton>
						</ListItem>
					)}
					<ItemBreak />
					{adminLinks.map((link) => (
						<ListItem key={link.text} disablePadding>
							<ListItemButton
								onClick={() => {
									props.onNavigate(link);
									navigate(buildNavPath(link.to), {
										state: { title: link.text },
									});
								}}
							>
								<ListItemText primary={link.text} />
							</ListItemButton>
						</ListItem>
					))}
					{userContext?.isSuperUser && (
						<ListItem key={"clear-cache"} disablePadding>
							<ListItemButton onClick={API.Data.clearCache}>
								<ListItemText primary={"Clear Cache"} />
							</ListItemButton>
						</ListItem>
					)}
					{adminLinks.length > 0 && <ItemBreak />}
					<ListItem key={"version"}>
						<ListItemText primary={<AppVersion />} />
					</ListItem>
				</List>
			</Box>
		</Drawer>
	);
};
